import React from "react";
import {
  X,
  ExternalLink,
  TrendingUp,
  TrendingDown,
  Minus,
  AlertTriangle,
  Thermometer,
  Snowflake,
  Leaf,
} from "lucide-react";
import {
  TemperatureReading,
  IceMeltingReading,
} from "../services/temperatureService";
import { ENSOStatus } from "../utils/fetchENSOStatus";

export type CardDetailType = "species" | "enso" | "temperature" | "ice";

interface SpeciesData {
  totalEndangered: number;
  criticallyEndangered: number;
  regionsTracked?: number;
}

interface CardDetailModalProps {
  open: boolean;
  onClose: () => void;
  type: CardDetailType;
  speciesData?: SpeciesData;
  ensoData?: ENSOStatus | null;
  temperatureData?: TemperatureReading | null;
  iceMeltingData?: IceMeltingReading | null;
}

// ─── Theme configs ──────────────────────────────────────────────────────────

const themes = {
  species: {
    gradient: "from-red-600 to-red-800",
    accent: "text-red-500",
    bg: "bg-red-50 dark:bg-red-950/30",
    border: "border-red-200 dark:border-red-800/50",
    badge: "bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-300",
    statBg: "bg-red-50 dark:bg-red-950/40",
    icon: Leaf,
  },
  enso: {
    gradient: "from-amber-500 to-orange-600",
    accent: "text-amber-500",
    bg: "bg-amber-50 dark:bg-amber-950/30",
    border: "border-amber-200 dark:border-amber-800/50",
    badge:
      "bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-300",
    statBg: "bg-amber-50 dark:bg-amber-950/40",
    icon: AlertTriangle,
  },
  temperature: {
    gradient: "from-blue-600 to-blue-800",
    accent: "text-blue-500",
    bg: "bg-blue-50 dark:bg-blue-950/30",
    border: "border-blue-200 dark:border-blue-800/50",
    badge: "bg-blue-100 text-blue-700 dark:bg-blue-900/40 dark:text-blue-300",
    statBg: "bg-blue-50 dark:bg-blue-950/40",
    icon: Thermometer,
  },
  ice: {
    gradient: "from-cyan-500 to-cyan-700",
    accent: "text-cyan-500",
    bg: "bg-cyan-50 dark:bg-cyan-950/30",
    border: "border-cyan-200 dark:border-cyan-800/50",
    badge: "bg-cyan-100 text-cyan-700 dark:bg-cyan-900/40 dark:text-cyan-300",
    statBg: "bg-cyan-50 dark:bg-cyan-950/40",
    icon: Snowflake,
  },
};

// ─── Section helper ──────────────────────────────────────────────────────────

const Section = ({
  title,
  children,
}: {
  title: string;
  children: React.ReactNode;
}) => (
  <div className="mb-6">
    <h4 className="text-xs font-semibold uppercase tracking-widest text-muted-foreground mb-3">
      {title}
    </h4>
    {children}
  </div>
);

const StatPill = ({
  label,
  value,
  sub,
  accent,
}: {
  label: string;
  value: string;
  sub?: string;
  accent?: string;
}) => (
  <div className="flex flex-col items-center justify-center rounded-xl border border-border bg-card/60 px-4 py-3 text-center">
    <span className={`text-xl font-bold leading-none ${accent || ""}`}>
      {value}
    </span>
    {sub && (
      <span className="text-[0.65rem] text-muted-foreground mt-0.5">{sub}</span>
    )}
    <span className="text-[0.7rem] text-muted-foreground mt-1 font-medium">
      {label}
    </span>
  </div>
);

const InfoRow = ({ label, value }: { label: string; value: string }) => (
  <div className="flex items-start justify-between gap-4 py-2 border-b border-border/50 last:border-0">
    <span className="text-sm text-muted-foreground shrink-0">{label}</span>
    <span className="text-sm text-foreground text-right font-medium">
      {value}
    </span>
  </div>
);

// ─── Modal content by type ───────────────────────────────────────────────────

function SpeciesContent({ data }: { data?: SpeciesData }) {
  const theme = themes.species;
  return (
    <>
      <Section title="Key Metrics">
        <div className="grid grid-cols-2 gap-3">
          <StatPill
            label="Total Endangered"
            value={data?.totalEndangered.toLocaleString() ?? "—"}
            accent="text-red-500"
          />
          <StatPill
            label="Critically Endangered"
            value={data?.criticallyEndangered.toLocaleString() ?? "—"}
            accent="text-orange-500"
            sub="CR category"
          />
        </div>
      </Section>

      <Section title="What This Measures">
        <p className="text-sm text-muted-foreground leading-relaxed">
          The Priority Species metric aggregates species classified as
          Endangered (EN) or Critically Endangered (CR) on the IUCN Red List,
          across{" "}
          {data?.regionsTracked != null ? (
            <span className="text-2xl font-bold text-red-500 mx-1">
              {data.regionsTracked.toLocaleString()}
            </span>
          ) : (
            <span className="text-2xl font-bold text-red-500 mx-1">—</span>
          )}
          geospatial regions currently indexed in our database, ensuring a
          globally representative view of biodiversity pressure.
        </p>
      </Section>

      <Section title="IUCN Threat Categories">
        <p className="text-xs text-muted-foreground mb-3">
          This metric counts only{" "}
          <span className="font-semibold text-foreground">CR</span> and{" "}
          <span className="font-semibold text-foreground">EN</span> categories.
          EX and VU are shown for scale.
        </p>
        <div className="space-y-2 text-left">
          {[
            {
              code: "EX",
              label: "Extinct",
              desc: "No reasonable doubt that the last individual has died.",
              color: "bg-gray-400",
              tracked: false,
            },
            {
              code: "CR",
              label: "Critically Endangered",
              desc: "Extremely high risk of extinction in the wild.",
              color: "bg-red-600",
              tracked: true,
            },
            {
              code: "EN",
              label: "Endangered",
              desc: "Very high risk of extinction in the wild.",
              color: "bg-orange-500",
              tracked: true,
            },
            {
              code: "VU",
              label: "Vulnerable",
              desc: "High risk of endangerment in the wild.",
              color: "bg-amber-400",
              tracked: false,
            },
          ].map((cat) => (
            <div key={cat.code} className="flex items-start gap-3">
              <span
                className={`mt-0.5 flex h-5 w-8 items-center justify-center rounded text-[0.6rem] font-bold text-white shrink-0 ${cat.color}`}
              >
                {cat.code}
              </span>
              <div className="flex-1">
                <div className="flex items-center gap-2">
                  <span className="text-sm font-semibold text-foreground">
                    {cat.label}
                  </span>
                  {cat.tracked && (
                    <span className="text-[0.6rem] font-semibold px-1.5 py-0.5 rounded-full bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-300">
                      tracked
                    </span>
                  )}
                </div>
                <p className="text-xs text-muted-foreground">{cat.desc}</p>
              </div>
            </div>
          ))}
        </div>
      </Section>

      <Section title="Why It Matters">
        <p className="text-sm text-muted-foreground leading-relaxed">
          Biodiversity underpins every ecosystem service that supports human
          life — clean water, food security, climate regulation, and disease
          control. Each species lost represents an irreversible collapse in the
          web of life. Monitoring these populations enables early intervention
          and informs conservation policy before local extinction becomes
          regional.
        </p>
      </Section>

      <Section title="Data Source">
        <InfoRow label="Provider" value="IUCN Red List of Threatened Species" />
        <InfoRow label="Scope" value="Global · All Geospatial Zones" />
        <InfoRow label="Categories Tracked" value="EN + CR combined total" />
        <a
          href="https://www.iucnredlist.org"
          target="_blank"
          rel="noopener noreferrer"
          className="mt-3 inline-flex items-center gap-1.5 text-xs text-red-500 hover:underline font-medium"
        >
          Visit IUCN Red List <ExternalLink size={11} />
        </a>
      </Section>
    </>
  );
}

function ENSOContent({ data }: { data?: ENSOStatus | null }) {
  const isElNino = data?.phase === "El Niño";
  const isLaNina = data?.phase === "La Niña";
  const phaseColor = isElNino
    ? "text-red-500"
    : isLaNina
      ? "text-blue-500"
      : "text-zinc-400";
  const TrendIcon =
    data?.trend === "strengthening"
      ? TrendingUp
      : data?.trend === "weakening"
        ? TrendingDown
        : Minus;

  return (
    <>
      <Section title="Current Conditions">
        <div className="grid grid-cols-3 gap-3">
          <StatPill
            label="Phase"
            value={data?.phase ?? "—"}
            accent={phaseColor}
          />
          <StatPill
            label="Strength"
            value={data?.strength ?? "—"}
            accent="text-foreground"
          />
          <StatPill
            label="Niño 3.4"
            value={
              data?.currentValue != null
                ? `${data.currentValue > 0 ? "+" : ""}${data.currentValue.toFixed(2)}`
                : "—"
            }
            sub="°C anomaly"
            accent={
              (data?.currentValue ?? 0) > 0
                ? "text-red-500"
                : (data?.currentValue ?? 0) < 0
                  ? "text-blue-500"
                  : "text-foreground"
            }
          />
        </div>
        {data?.trend && (
          <div className="mt-3 flex items-center gap-2 text-sm text-muted-foreground">
            <TrendIcon size={14} className="shrink-0" />
            <span>
              Trend:{" "}
              <span className="font-medium text-foreground capitalize">
                {data.trend}
              </span>
            </span>
          </div>
        )}
      </Section>

      <Section title="What Is ENSO?">
        <p className="text-sm text-muted-foreground leading-relaxed">
          ENSO (El Niño–Southern Oscillation) is a climate cycle in the tropical
          Pacific that alternates between warmer (El Niño) and cooler (La Niña)
          sea surface temperatures. It is the single largest driver of
          year-to-year variability in global climate, affecting rainfall,
          droughts, hurricanes, and temperatures on every continent.
        </p>
      </Section>

      <Section title="El Niño vs La Niña">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div className="rounded-xl border border-red-200 dark:border-red-800/40 bg-red-50 dark:bg-red-950/20 p-4">
            <p className="text-sm font-semibold text-red-600 dark:text-red-400 mb-1">
              El Niño
            </p>
            <p className="text-xs text-muted-foreground leading-relaxed">
              Warmer-than-average Pacific SSTs (ONI ≥ +0.5°C). Linked to
              droughts in Australia and Southeast Asia, floods in South America,
              and a suppressed Atlantic hurricane season.
            </p>
          </div>
          <div className="rounded-xl border border-blue-200 dark:border-blue-800/40 bg-blue-50 dark:bg-blue-950/20 p-4">
            <p className="text-sm font-semibold text-blue-600 dark:text-blue-400 mb-1">
              La Niña
            </p>
            <p className="text-xs text-muted-foreground leading-relaxed">
              Cooler-than-average Pacific SSTs (ONI ≤ −0.5°C). Associated with
              above-average rainfall in Southeast Asia, droughts in South
              America, and an active Atlantic hurricane season.
            </p>
          </div>
        </div>
      </Section>

      {data?.impact && (
        <Section title="Current Regional Impact">
          <div className="rounded-xl border border-border bg-card/60 p-4">
            <p className="text-sm text-foreground leading-relaxed">
              {data.impact}
            </p>
          </div>
        </Section>
      )}

      <Section title="Data Source">
        <InfoRow label="Provider" value="NOAA Climate Prediction Center" />
        <InfoRow label="Index Used" value="Oceanic Niño Index (ONI)" />
        <InfoRow label="Threshold" value="±0.5°C over 3-month running mean" />
        {data?.updated && <InfoRow label="Last Updated" value={data.updated} />}
        <a
          href="https://www.cpc.ncep.noaa.gov/products/analysis_monitoring/ensostuff/ONI_v5.php"
          target="_blank"
          rel="noopener noreferrer"
          className="mt-3 inline-flex items-center gap-1.5 text-xs text-amber-500 hover:underline font-medium"
        >
          View NOAA ONI Data <ExternalLink size={11} />
        </a>
      </Section>
    </>
  );
}

function TemperatureContent({ data }: { data?: TemperatureReading | null }) {
  const val = data?.value;
  const unit = data?.unit === "celsius" ? "°C" : "°F";
  const preIndustrial = 13.9; // °C pre-industrial baseline
  const anomaly = val != null ? (val - preIndustrial).toFixed(2) : null;

  return (
    <>
      <Section title="Current Reading">
        <div className="grid grid-cols-2 gap-3">
          <StatPill
            label="Global Avg Surface Temp"
            value={val != null ? `${val.toFixed(1)}${unit}` : "—"}
            accent="text-blue-500"
          />
          <StatPill
            label="Anomaly vs Pre-Industrial"
            value={anomaly != null ? `+${anomaly}°C` : "—"}
            sub="vs ~1850–1900 baseline"
            accent={
              anomaly != null && parseFloat(anomaly) >= 1.5
                ? "text-red-500"
                : "text-orange-500"
            }
          />
        </div>
        {data?.location && (
          <p className="mt-3 text-xs text-muted-foreground">
            Coverage:{" "}
            <span className="font-medium text-foreground">{data.location}</span>
          </p>
        )}
      </Section>

      <Section title="What This Measures">
        <p className="text-sm text-muted-foreground leading-relaxed">
          The Average Surface Temperature is the mean near-surface air
          temperature across the globe, blending land and sea-surface
          measurements. It is the most-cited indicator of long-term climate
          change and forms the basis of international climate targets set by the
          Paris Agreement.
        </p>
      </Section>

      <Section title="Climate Context">
        <div className="space-y-0">
          <InfoRow
            label="Pre-industrial baseline"
            value="~13.9 °C (1850–1900)"
          />
          <InfoRow
            label="Paris Agreement target"
            value="Limit warming to +1.5 °C"
          />
          <InfoRow
            label="Current anomaly"
            value={anomaly != null ? `+${anomaly} °C above baseline` : "—"}
          />
        </div>
      </Section>

      <Section title="Why It Matters">
        <p className="text-sm text-muted-foreground leading-relaxed">
          Every 0.5°C of additional warming brings significantly more extreme
          heat events, intensified precipitation, and accelerated sea level
          rise. Crossing the +1.5°C threshold risks triggering tipping points in
          ecosystems — from coral bleaching to permafrost thaw — with cascading
          effects on agriculture, water security, and human health.
        </p>
      </Section>

      <Section title="Data Source">
        {data?.label && <InfoRow label="Attribution" value={data.label} />}
        <InfoRow label="Cadence" value="NASA GISTEMP publishes monthly" />
        <InfoRow label="Cache" value="Refreshed from source every 30 days" />
        <a
          href="https://data.giss.nasa.gov/gistemp/"
          target="_blank"
          rel="noopener noreferrer"
          className="mt-3 inline-flex items-center gap-1.5 text-xs text-blue-500 hover:underline font-medium"
        >
          NASA GISTEMP Surface Temperature Analysis <ExternalLink size={11} />
        </a>
      </Section>
    </>
  );
}

function IceContent({ data }: { data?: IceMeltingReading | null }) {
  const rate = data?.rate_gigatons_annually;

  return (
    <>
      <Section title="Ice Sheet Loss (IMBIE 2023)">
        <div className="grid grid-cols-2 gap-3">
          <StatPill
            label="Ice Sheet Loss"
            value={rate != null ? `${rate.toFixed(0)} Gt/yr` : "416 Gt/yr"}
            sub="Greenland + Antarctica only"
            accent="text-cyan-500"
          />
          <StatPill
            label="Sea Level Contribution"
            value="~1.2 mm/yr"
            sub="from ice sheets alone"
            accent="text-cyan-400"
          />
        </div>
        <p className="mt-3 text-xs text-muted-foreground leading-relaxed">
          This figure covers polar ice sheets only. Adding mountain glaciers
          (~335 Gt/yr, Hugonnet et al. 2021) brings total global ice loss to
          approximately{" "}
          <span className="font-semibold text-foreground">~750 Gt/yr</span>.
        </p>
      </Section>

      <Section title="What This Measures">
        <p className="text-sm text-muted-foreground leading-relaxed">
          The tracked value is the combined mass loss from the Greenland and
          Antarctic ice sheets, expressed in gigatons per year (Gt/yr), as
          reported by the IMBIE 2023 Phase 3 consortium. One gigaton equals one
          billion metric tons of ice. Mountain glaciers are measured separately
          and are not included in this figure.
        </p>
      </Section>

      <Section title="Ice Loss Breakdown">
        <div className="space-y-3 text-left">
          {[
            {
              region: "Greenland Ice Sheet",
              rate: "~267 Gt/yr",
              sub: "IMBIE 2023 · 2006–2020 avg",
              detail:
                "Accelerating due to warmer summers and increased ocean heat flux at marine-terminating glaciers.",
              color: "bg-cyan-500",
            },
            {
              region: "Antarctic Ice Sheet",
              rate: "~149 Gt/yr",
              sub: "IMBIE 2023 · 1992–2020 avg",
              detail:
                "West Antarctica contributes the most, driven by warm ocean water beneath ice shelves.",
              color: "bg-blue-500",
            },
            {
              region: "Mountain Glaciers",
              rate: "~335 Gt/yr",
              sub: "Hugonnet et al. 2021 · not included above",
              detail:
                "Glaciers outside the polar ice sheets are losing mass at record rates. Tracked separately from ice sheets.",
              color: "bg-indigo-400",
            },
          ].map((item) => (
            <div key={item.region} className="flex items-start gap-3">
              <span
                className={`mt-1 h-2.5 w-2.5 rounded-full shrink-0 ${item.color}`}
              />
              <div>
                <div className="flex items-baseline gap-2 flex-wrap">
                  <span className="text-sm font-semibold text-foreground">
                    {item.region}
                  </span>
                  <span className="text-xs text-cyan-500 font-medium">
                    {item.rate}
                  </span>
                  <span className="text-[0.65rem] text-muted-foreground">
                    {item.sub}
                  </span>
                </div>
                <p className="text-xs text-muted-foreground leading-relaxed mt-0.5">
                  {item.detail}
                </p>
              </div>
            </div>
          ))}
        </div>
      </Section>

      <Section title="Sea Level Impact">
        <p className="text-sm text-muted-foreground leading-relaxed">
          If all land-based ice melted, global sea levels would rise by
          approximately 65 metres. At current rates, projections indicate 0.3–1
          metre of rise by 2100, threatening hundreds of millions of coastal
          residents and low-lying nations. Every gigaton of ice loss translates
          to roughly 0.0028 mm of global sea level rise.
        </p>
      </Section>

      <Section title="Data Source">
        {data?.label && <InfoRow label="Attribution" value={data.label} />}
        <InfoRow label="Ice sheets" value="IMBIE 2023 Phase 3 consortium" />
        <InfoRow label="Mountain glaciers" value="Hugonnet et al. 2021" />
        <InfoRow
          label="Verification"
          value="ESA CryoSat · NASA GRACE-FO · NSIDC"
        />
        <InfoRow label="Units" value="Gigatons per year (Gt/yr)" />
        <a
          href="https://nsidc.org/cryosphere/sotc/ice_sheets.html"
          target="_blank"
          rel="noopener noreferrer"
          className="mt-3 inline-flex items-center gap-1.5 text-xs text-cyan-500 hover:underline font-medium"
        >
          NSIDC: State of the Cryosphere <ExternalLink size={11} />
        </a>
      </Section>
    </>
  );
}

// ─── Modal titles / subtitles ────────────────────────────────────────────────

const META: Record<
  CardDetailType,
  {
    title: string;
    subtitle: string;
    learnMoreUrl: string;
    learnMoreLabel: string;
  }
> = {
  species: {
    title: "Priority Species",
    subtitle: "Global biodiversity threat tracking",
    learnMoreUrl: "https://www.iucnredlist.org",
    learnMoreLabel: "IUCN Red List",
  },
  enso: {
    title: "ENSO Status",
    subtitle: "El Niño–Southern Oscillation monitor",
    learnMoreUrl:
      "https://www.cpc.ncep.noaa.gov/products/analysis_monitoring/ensostuff/ONI_v5.php",
    learnMoreLabel: "NOAA CPC",
  },
  temperature: {
    title: "Avg Surface Temperature",
    subtitle: "Global mean surface air temperature",
    learnMoreUrl: "https://data.giss.nasa.gov/gistemp/",
    learnMoreLabel: "NASA GISTEMP",
  },
  ice: {
    title: "Ice Melting Rate",
    subtitle: "Annual polar ice sheet mass loss (IMBIE 2023)",
    learnMoreUrl: "https://nsidc.org/cryosphere/sotc/ice_sheets.html",
    learnMoreLabel: "NSIDC Cryosphere",
  },
};

// ─── Main modal ──────────────────────────────────────────────────────────────

export default function CardDetailModal({
  open,
  onClose,
  type,
  speciesData,
  ensoData,
  temperatureData,
  iceMeltingData,
}: CardDetailModalProps) {
  const theme = themes[type];
  const meta = META[type];
  const Icon = theme.icon;

  // Close on Escape
  React.useEffect(() => {
    if (!open) return;
    const handler = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [open, onClose]);

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-[200] flex items-end sm:items-center justify-center p-0 sm:p-4"
      role="dialog"
      aria-modal="true"
      aria-label={meta.title}
    >
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-black/60 backdrop-blur-sm"
        onClick={onClose}
        aria-hidden="true"
      />

      {/* Panel */}
      <div className="relative z-10 w-full sm:max-w-lg bg-background rounded-t-2xl sm:rounded-2xl shadow-2xl flex flex-col max-h-[92dvh] sm:max-h-[85vh] overflow-hidden border border-border/50 animate-in slide-in-from-bottom-4 duration-300">
        {/* ── Header ── */}
        <div
          className={`bg-gradient-to-r ${theme.gradient} px-6 py-5 shrink-0`}
        >
          <div className="flex items-start justify-between gap-4">
            <div className="flex items-center gap-3">
              <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-white/15">
                <Icon size={20} className="text-white" />
              </div>
              <div>
                <h2 className="text-lg font-bold text-white leading-tight">
                  {meta.title}
                </h2>
                <p className="text-xs text-white/70 mt-0.5">{meta.subtitle}</p>
              </div>
            </div>
            <button
              onClick={onClose}
              className="mt-0.5 flex h-8 w-8 items-center justify-center rounded-full bg-white/15 text-white hover:bg-white/30 transition-colors shrink-0"
              aria-label="Close"
            >
              <X size={16} />
            </button>
          </div>
        </div>

        {/* ── Scrollable body ── */}
        <div className="flex-1 overflow-y-auto px-6 py-5">
          {type === "species" && <SpeciesContent data={speciesData} />}
          {type === "enso" && <ENSOContent data={ensoData} />}
          {type === "temperature" && (
            <TemperatureContent data={temperatureData} />
          )}
          {type === "ice" && <IceContent data={iceMeltingData} />}
        </div>

        {/* ── Footer ── */}
        <div className="shrink-0 border-t border-border px-6 py-4 flex items-center justify-between gap-4 bg-muted/20">
          <p className="text-xs text-muted-foreground">
            Data refreshes automatically with each session.
          </p>
          <a
            href={meta.learnMoreUrl}
            target="_blank"
            rel="noopener noreferrer"
            className={`inline-flex items-center gap-1.5 text-xs font-semibold ${theme.accent} hover:underline shrink-0`}
          >
            {meta.learnMoreLabel} <ExternalLink size={11} />
          </a>
        </div>
      </div>
    </div>
  );
}
